// UserLivestreamBadges.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import DecryptionWrapper from '../container/DecryptionWrapper.js';

import Tooltip from '../component/Tooltip.jsx';
import AnimatedBadge from '../component/AnimatedBadge.jsx';

import {
  MediaAssetFormat,
  getBadge,
  getAnimatedBadgeSprite,
} from '../resource/getMediaAsset.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import defaults from '../resource/defaults.js';
import { isAnimatedBadge } from '../selector/getUserBadges.js';

import { color } from '../style/variables.js';
import { breakpoint } from '../style/variables.js';

export const USER_BADGE_SIZE = 20;

const tooltipStyle = {
  wrapper: {
    height: `${USER_BADGE_SIZE}px`,
    zIndex: 2,
    flex: 'none',
  },
};

export const UserLivestreamBadges = ({
  badges = defaults.EMPTY_ARRAY,
  deviceWidth = 0,
  publicBadgeUrlPath = '',
}) => {
  const { t } = useTranslation();
  return badges.slice(-2).map(badge => {
    const isAnimated = isAnimatedBadge({ badge });
    const src = isAnimated
      ? getAnimatedBadgeSprite({
          badge,
          path: publicBadgeUrlPath,
        }).href
      : getBadge({
          badge,
          size: 64,
          format: MediaAssetFormat.PNG,
          path: publicBadgeUrlPath,
        }).href;
    const i18nKey = getTranslationKey({ key: `badge_${badge}` });
    return (
      <Tooltip
        style={tooltipStyle}
        key={badge}
        position="bottom-center"
        renderHint={() => (
          <TooltipText>
            {t(i18nKey, {
              ns: TranslationNamespace.LIVESTREAM,
            })}
          </TooltipText>
        )}
        disabled={deviceWidth <= breakpoint.tablet}
        trigger="hover"
      >
        <DecryptionWrapper resourceUrl={src}>
          {isAnimated ? (
            <AnimatedBadge
              src={src}
              size={USER_BADGE_SIZE}
              alt={t(i18nKey, {
                ns: TranslationNamespace.LIVESTREAM,
              })}
            />
          ) : (
            <UserBadge
              src={src}
              alt={t(i18nKey, {
                ns: TranslationNamespace.LIVESTREAM,
              })}
              aria-hidden
            />
          )}
        </DecryptionWrapper>
      </Tooltip>
    );
  });
};

UserLivestreamBadges.propTypes = {
  publicBadgeUrlPath: PropTypes.string,
  deviceWidth: PropTypes.number,
  badges: PropTypes.array,
};

export const UserBadge = styled.img`
  position: relative;
  width: ${USER_BADGE_SIZE}px;
  height: ${USER_BADGE_SIZE}px;
  object-fit: contain;
`;

const TooltipText = styled.div`
  position: absolute;
  z-index: 1;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${color.neutral[100]};
  color: ${color.black};
  text-align: center;
  white-space: nowrap;
  font-size: 12px;
  transform: translate(-50%, -52px);
`;

export default UserLivestreamBadges;
